import { type ReactNode, useId } from 'react'
import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogContent,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { Button } from '@/components/Button'
import { SectionHeadline } from '@/components/SectionHeadline'
import { CardList } from '@/components/Card'

export interface OpenItemsDialogProps {
  children?: ReactNode
  isOpen: boolean
  onClose: () => unknown
  isChecklistComplete?: boolean
  testId?: string
  closeTestId?: string
}

/**
 * Dialog for viewing an incomplete checklist.
 */
export function OpenItemsDialog({
  children,
  isOpen,
  onClose,
  isChecklistComplete,
  testId,
  closeTestId,
}: OpenItemsDialogProps) {
  return (
    <ModalDialog
      isOpen={isOpen}
      onClose={onClose}
      sheetSizing={isChecklistComplete ? 'auto' : 'stretch'}
      size="lg"
      testId={testId}
    >
      <ModalDialogHeader>
        {isChecklistComplete ? (
          <>
            <ModalDialogTitle>Your checklist is complete!</ModalDialogTitle>
            <ModalDialogDescription>
              There are no missing items left, great work!
            </ModalDialogDescription>
          </>
        ) : (
          <>
            <ModalDialogTitle>
              Your checklist is almost complete
            </ModalDialogTitle>
            <ModalDialogDescription>
              To avoid unnecessary delays, please provide the following missing
              items.
            </ModalDialogDescription>
          </>
        )}
      </ModalDialogHeader>
      <ModalDialogContent>{children}</ModalDialogContent>
      <ModalDialogFooter>
        <ModalDialogActions>
          <Button
            label="Close"
            variant="outline"
            onPress={onClose}
            testId={closeTestId}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}

interface OpenItemsDialogChecklistSectionProps {
  title: string
  children?: ReactNode
}

/**
 * A section of items in the {@link OpenItemsDialog}.
 * Children must be {@link CardListItem}s.
 */
export function OpenItemsDialogChecklistSection({
  title,
  children,
}: OpenItemsDialogChecklistSectionProps) {
  const headlineId = useId()
  return (
    <section className="pb-lg">
      <SectionHeadline id={headlineId}>{title}</SectionHeadline>
      <CardList aria-labelledby={headlineId}>{children}</CardList>
    </section>
  )
}
